import { StackChartSeries } from '@/components/chart/types';
import { ApiResponse } from '@/react-query/context/ApiClientContextController';
import { useQuery } from '@/react-query/hooks/useQuery/useQuery';
import { InfoCard } from '@/services/dashboard/overview/overview.types';
import { SalesAnalyticsParams, salesPerformanceQueries } from '@/services/salesPerformance/salesPerformance.queries';
import { LocationWiseSalesAnalyticsItem } from '@/services/salesPerformance/salesPerformance.types';
import { filterWeekData, formatRangeDate, getWeekRange } from '../utils/weekRange';

type ChartDataItem = {
  [key: string]: string | number;
  day: string;
};

const normalizeKey = (_name: string, index: number) => `location-${index}`;

const defaultColors = [
  'var(--chart-sales-1)',
  'var(--chart-sales-2)',
  'var(--chart-sales-3)',
  'var(--chart-sales-4)',
  'var(--chart-sales-5)',
];

export const useSalesPerformance = (): { salesPerformance: InfoCard; isLoadingSalesInfo: boolean } => {
  const { data, isLoading: isLoadingSalesInfo } = useQuery<ApiResponse<InfoCard>>({
    ...salesPerformanceQueries.salesPerformanceOverview(),
    select: (res) => ({ ...res, data: res.data }),
  });

  return { salesPerformance: data?.data || ({} as InfoCard), isLoadingSalesInfo };
};

export const useLocationWiseSalesAnalytics = (
  weekOffset = 0,
): {
  locationWiseSalesAnalytics: StackChartSeries[];
  chartData: ChartDataItem[];
  isLoadingSalesAnalytics: boolean;
  dateRange: string;
  chartLabels: string[];
} => {
  const { start, end } = getWeekRange(new Date(), weekOffset);
  const params: SalesAnalyticsParams = {
    fromDate: start.getTime(),
    toDate: end.getTime(),
  };

  const { data, isLoading: isLoadingSalesAnalytics } = useQuery({
    ...salesPerformanceQueries.locationWiseSalesAnalytics(params),
    select: (res) => ({ ...res, data: res.data }),
  });

  const rawData = data?.data?.locationWiseSales ?? [];
  const filteredData = filterWeekData(rawData, start, end);
  const dayMs = 24 * 60 * 60 * 1000;
  // create chart dates at UTC midnight to avoid timezone shifts
  const startUtcMs = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
  const chartDates = Array.from({ length: 7 }, (_, index) => new Date(startUtcMs + index * dayMs));
  const chartLabels = chartDates.map((date) => formatRangeDate(date));
  const dateRange = `${formatRangeDate(start)} ~ ${formatRangeDate(end)}`;

  const locationNames = Array.from(
    filteredData.reduce<Set<string>>((set, item) => {
      item.locations.forEach((loc) => set.add(loc.locationName));
      return set;
    }, new Set()),
  ).sort();

  const dataByDateKey = new Map<string, LocationWiseSalesAnalyticsItem[]>(
    filteredData.reduce((acc, item) => {
      // normalize incoming dates to ISO YYYY-MM-DD in UTC to match chartDates keys
      const date = new Date(Number(item.date));
      const key = date.toISOString().slice(0, 10);
      const existing = acc.get(key) ?? [];
      acc.set(key, [...existing, item]);
      return acc;
    }, new Map<string, LocationWiseSalesAnalyticsItem[]>()),
  );

  const locationWiseSalesAnalytics: StackChartSeries[] = locationNames.map((name, index) => ({
    key: normalizeKey(name, index),
    name,
    color: defaultColors[index % defaultColors.length],
    values: chartDates.map((date) => {
      const dateKey = date.toISOString().slice(0, 10);
      const itemsForDate = dataByDateKey.get(dateKey) ?? [];
      // aggregate all amounts for this location across all items on this date
      const totalAmount = itemsForDate.reduce((sum, item) => {
        const locationAmount = item.locations.find((entry) => entry.locationName === name)?.amount ?? 0;
        return sum + locationAmount;
      }, 0);
      return totalAmount;
    }),
  }));

  const chartData: ChartDataItem[] = chartLabels.map((day, i) => {
    const item: ChartDataItem = { day };
    locationWiseSalesAnalytics.forEach((series) => {
      item[series.key] = series.values[i];
    });
    return item;
  });

  return { locationWiseSalesAnalytics, chartData, dateRange, chartLabels, isLoadingSalesAnalytics };
};
